<template lang="html">
  <div>
    <v-card flat>
      <v-card-title class="secondary white--text">
        <div class="ma-0">
          <div v-if="unreconciledPaymentsRecords" class="d-flex align-center">
            <v-icon large left color="white">mdi-transfer</v-icon>
            <p class="subtitle-1 my-0">{{ unreconciledPaymentsRecords.Count }} payments</p>
            <v-divider vertical class="mx-3 my-1" color="white"></v-divider>
            <span class="subtitle-1">{{ totalPayments }}</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <TableSearch @input="(newText) => {search = newText}" />
      </v-card-title>

      <!-- Date Filters Row -->
      <v-card-text class="py-2 px-4 tertiary">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="5" md="4" class="pr-2">
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fromDate"
                  label="From Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="handleFromDateClear"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromDate"
                @input="fromDateMenu = false; handleFilterChange()"
                no-title
                scrollable
              ></v-date-picker>
            </v-menu>
          </v-col>
          
          <v-col cols="12" sm="5" md="4" class="px-2">
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="toDate"
                  label="To Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="handleToDateClear"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="toDate"
                @input="toDateMenu = false; handleFilterChange()"
                no-title
                scrollable
              ></v-date-picker>
            </v-menu>
          </v-col>
          
          <v-col cols="12" sm="2" md="4" class="pl-2 text-right">
            <v-btn
              v-if="fromDate || toDate"
              text
              small
              color="secondary"
              @click="clearAllFilters"
            >
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Table -->
      <v-data-table
        :headers="headers"
        :items="displayItems"
        :loading="isLoading"
        :search="search"
        :server-items-length="unreconciledPaymentsRecords.Count || 0"
        :options.sync="tableOptions"
        @update:options="updateOptions"
        hide-default-footer
        class="elevation-0"
        no-data-text="No payments found"
      >
        <template v-slot:item.PaymentDate="{ item }">
          <span>
            {{ item.PaymentDate | formatDate }}
          </span>
        </template>

        <template v-slot:item.Amount="{ item }">
          <span :class="getColor(item.ColourStatus)">
            {{ item | returnDisplayAmount}}
          </span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="secondary"
                dark
                small
                depressed
                v-bind="attrs"
                v-on="on"
                class="text-capitalize"
              >
                Actions
                <v-icon right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list class="py-0 secondary" dark>
              <v-list-item @click="openDialog(item)">
                <v-list-item-title>Contact support regarding this payment</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-comment-text-outline</v-icon>
                </v-list-item-icon>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>

        <!-- Custom Pagination Controls -->
        <v-card-actions class="pt-0">
          <v-spacer></v-spacer>
          <div class="d-flex align-center">
            <!-- Items per page selector -->
            <span class="grey--text caption mr-2">Items per page:</span>
            <v-select
              :value="localItemsPerPage"
              :items="[10, 25, 50]"
              @change="handleItemsPerPageChange"
              hide-details
              dense
              style="max-width: 60px"
              class="mr-4"
            ></v-select>

            <!-- Page info -->
            <span class="mx-2 caption">
              {{ pageStart }}-{{ pageEnd }} of {{ unreconciledPaymentsRecords.Count || 0 }}
            </span>

            <!-- Page navigation buttons -->
            <v-btn
              icon
              small
              :disabled="localPage === 1 || isLoading"
              @click="handlePageChange(localPage - 1)"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              :disabled="localPage >= totalPages || isLoading"
              @click="handlePageChange(localPage + 1)"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card-actions>
    </v-card>

    <MsgDialog
      :dialog="dialog"
      :details="selectedItem.DisplayPaymentAmount"
      :source="source"
      :OID="selectedItem.OID"
      @closeDialog="closeDialog"
      @msgSent="msgSent"
    />

    <Snackbar 
      :snackbarDialog="snackbar" 
      :snackbarText="snackbarText" 
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar()"
      :timeout="timeout"
    />

  </div>
</template>

<script>
import { mapState } from 'vuex'
import MsgDialog from '@/components/Dialogs/MessageDialog'
import Snackbar from '@/components/Snackbars/TheSnackbar'
import TableSearch from '@/components/TableComponents/TableSearch.vue'

export default {
  components: {
    MsgDialog,
    Snackbar,
    TableSearch
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({ page: 1, itemsPerPage: 10 })
    }
  },
  data() {
    return {
      source: 'Unreconciled payment',
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      timeout: 3000,
      loading: false,
      search: '',
      selectedItem: '',
      dialog: false,
      msgText: '',
      localPage: 1,
      localItemsPerPage: 10,
      isPending: false,
      tableOptions: {},
      headers: [
        { text: 'Bank Statement Ref.', value: 'StatementReference', sortable: false },
        { text: 'Date', value: 'PaymentDate', sortable: false },
        { text: 'Amount', value: 'Amount', sortable: false },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false }
      ],
      fromDate: '',
      toDate: '',
      fromDateMenu: false,
      toDateMenu: false
    }
  },
  methods: {
    closeSnackbar() {
      this.snackbar = false
    },
    openDialog(item) {
      this.selectedItem = item
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      this.msgText = ''
    },
    msgSent(payload) {
      this.dialog = false
      this.snackbar = true,
      this.snackbarColor = payload.color
      this.snackbarText = payload.text
    },
    getColor(color) {
     return color.toLowerCase() + '--text font-weight-medium'
    },
    handlePageChange(newPage) {
      if (this.isPending || newPage === this.localPage) return
      
      this.isPending = true
      this.localPage = newPage
      this.emitPaginationChange()
      
      // Reset pending state after a short delay
      setTimeout(() => {
        this.isPending = false
      }, 500)
    },
    handleItemsPerPageChange(newItemsPerPage) {
      if (this.isPending || newItemsPerPage === this.localItemsPerPage) return
      
      this.isPending = true
      this.localItemsPerPage = newItemsPerPage
      this.localPage = 1 // Reset to first page when changing items per page
      this.emitPaginationChange()
      
      // Reset pending state after a short delay
      setTimeout(() => {
        this.isPending = false
      }, 500)
    },
    emitPaginationChange() {
      // Only emit if search is empty (server-side pagination)
      if (this.search === "") {
        this.$emit('paginate', this.localPage, this.localItemsPerPage, this.fromDate, this.toDate);
      }
    },
    updateOptions() {
      // This method is called when table options change
      // We don't need to do anything here since we're using custom pagination
    },
    handleFilterChange() {
      // Reset to first page when filters change
      this.localPage = 1;
      this.$emit('filter-change', this.fromDate, this.toDate);
    },
    handleFromDateClear() {
      this.fromDate = '';
      this.handleFilterChange();
    },
    handleToDateClear() {
      this.toDate = '';
      this.handleFilterChange();
    },
    clearAllFilters() {
      this.fromDate = '';
      this.toDate = '';
      this.handleFilterChange();
    }
  },
  computed: {
    ...mapState({
      unreconciledPaymentsRecords: state => state.UnreconciledPayments.unreconciledPaymentsRecords
    }),
    totalPayments() {
      const array = this.unreconciledPaymentsRecords.Data
      const payments = array.map(val => {
        return val.PaymentAmount
      })
      const total = payments.reduce(
        (acc, val) => acc + val,
        0
      )
      // return total.toLocaleString()
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(total)
    },
    displayItems() {
      if (!this.unreconciledPaymentsRecords || !this.unreconciledPaymentsRecords.Data) return []
      
      // For server-side pagination, we show all data received
      // Search is handled client-side on the current page only
      if (!this.search) {
        return this.unreconciledPaymentsRecords.Data
      }
      
      const searchLower = this.search.toLowerCase()
      return this.unreconciledPaymentsRecords.Data.filter(item => {
        return Object.values(item).some(value => 
          String(value).toLowerCase().includes(searchLower)
        )
      })
    },
    totalPages() {
      if (!this.unreconciledPaymentsRecords || !this.unreconciledPaymentsRecords.Count) return 1
      return Math.ceil(this.unreconciledPaymentsRecords.Count / this.localItemsPerPage)
    },
    pageStart() {
      if (!this.unreconciledPaymentsRecords || !this.unreconciledPaymentsRecords.Count || this.unreconciledPaymentsRecords.Count === 0) return 0
      return ((this.localPage - 1) * this.localItemsPerPage) + 1
    },
    pageEnd() {
      if (!this.unreconciledPaymentsRecords || !this.unreconciledPaymentsRecords.Count) return 0
      const end = this.localPage * this.localItemsPerPage
      return end > this.unreconciledPaymentsRecords.Count ? this.unreconciledPaymentsRecords.Count : end
    }
  },
  watch: {
    pagination: {
      immediate: true,
      handler(val) {
        if (val) {
          this.localPage = val.page || 1
          this.localItemsPerPage = val.itemsPerPage || 10
        }
      }
    }
  },
  filters: {
    returnDisplayAmount(val) {
      return val.DisplayPaymentAmount
    }
  },

}
</script>

<style lang="css" scoped>
.position-relative {
  position: relative;
}
</style>
